/**
 * On-device storage, keyed by edition.
 *
 * This is a CACHE OF API RESPONSES, never a second source of truth. The
 * server is authoritative for content; the device is authoritative only for
 * what the user did while it could not reach the server, and that queue
 * drains on reconnect.
 *
 * Every store is scoped by edition id, so a user attending OZORA 2026 and
 * 2027 has two independent sets of everything.
 */
import Dexie, { type Table } from 'dexie';
import type { EditionBundle, AppEvent } from '@myrave/shared';

export interface StoredBundle {
  editionId: string;
  version: string;
  bundle: EditionBundle;
  fetchedAt: number;
  /**
   * The account that downloaded it (2026-10-16). Another account on this
   * device never sees it — a new account opened OZORA's live page from the
   * last person's copy (`bundle.ts` `ownStored`).
   */
  owner?: string | null;
}

export interface StoredFavorite {
  /** `${editionId}:${actSlug}` — the compound key IS the scoping. */
  id: string;
  editionId: string;
  actSlug: string;
  saved: boolean;
  updatedAt: number;
  /** False until the server has acknowledged it. */
  synced: boolean;
  /**
   * The account this change was made AS, at the moment it was queued — null
   * for nobody (an anonymous visitor, or a row from before this existed).
   * A drain sends only the signed-in account's rows, and adopts null ones: on
   * a shared phone, A's unsynced hearts must never be sent as B's
   * (`offline/sync.ts`). Not indexed — filtered in memory, no schema change.
   */
  userId?: string | null;
  /**
   * The server REFUSED the batch this record was in (a 4xx that is not about
   * the session): kept on this device, shown as not synced, and no longer
   * sent — resending the same refusal forever blocked every later change.
   * Cleared by the next change to the record (a fresh `put`).
   */
  rejected?: boolean;
}

export interface StoredPin {
  /** `${editionId}:${pinId}` — the compound key IS the scoping. */
  id: string;
  editionId: string;
  /** The pin's own id, assigned on the device that dropped it (or the server's, for an older pin). */
  pinId: string;
  label?: string;
  icon?: string;
  x: number;
  y: number;
  updatedAt: number;
  /** A tombstone: removed at this instant. Kept so another device cannot bring it back. */
  deletedAt?: number | null;
  /** False until the server has acknowledged it. */
  synced: boolean;
  /**
   * The account this change was made AS, at the moment it was queued — null
   * for nobody (an anonymous visitor, or a row from before this existed).
   * A drain sends only the signed-in account's rows, and adopts null ones: on
   * a shared phone, A's unsynced hearts must never be sent as B's
   * (`offline/sync.ts`). Not indexed — filtered in memory, no schema change.
   */
  userId?: string | null;
  /**
   * The server REFUSED the batch this record was in (a 4xx that is not about
   * the session): kept on this device, shown as not synced, and no longer
   * sent — resending the same refusal forever blocked every later change.
   * Cleared by the next change to the record (a fresh `put`).
   */
  rejected?: boolean;
}

/**
 * A statistics event waiting for signal (§62.3).
 *
 * Queued at the moment it happens and sent whenever there is a network — days
 * later, from a car park, is the normal case rather than the edge one. The id
 * is generated here, not at send time, because it is the server's idempotency
 * key: a batch retried over flaky wifi must not double a crowd count.
 */
export interface QueuedEventRow {
  id: string;
  editionId: string;
  event: AppEvent;
  queuedAt: number;
}

/**
 * A ticket kept on this phone (step 82) — the API's row, with MyRave's drawing of its code
 * (an SVG, so the gate needs no library and no signal) and whether that drawing read back
 * as the same code. Its account's only, as a stored bundle is (`owner`).
 */
export interface StoredTicket {
  id: string;
  editionId: string;
  owner: string | null;
  ticket: import('@/visitor/tickets/ticketRules').Ticket;
  /** MyRave's drawing, or null — this code is shown as the original. */
  svg: string | null;
  /** The drawing read back as the same code; false opens the gate on the original. */
  checked: boolean;
  /** A copy kept before tickets were kept for good (2026-10-29) may still carry its old deletion day; unread. */
  deleteAt?: string | null;
  /** Its original was fetched and read on this phone (drawing and file kept); false is tried again. */
  analysed: boolean;
  /** What the drawing was made from (`drawKey`: the design's version + the rave's logo); older → redrawn. */
  drawKey?: string;
}

/**
 * A ticket's original on this phone: the file as uploaded (`id` = its originalId), and for a
 * PDF each ticket's page drawn as a PNG (`id` = `<originalId>#<page>`), so the gate shows
 * the original with no pdf.js and no signal.
 */
export interface StoredTicketFile {
  id: string;
  editionId: string;
  owner: string | null;
  blob: Blob;
  type: string;
}

export interface AssetProgress {
  editionId: string;
  /** The bundle version the download was for — a new version is a new download. */
  version?: string;
  total: number;
  done: number;
  bytes: number;
  finishedAt?: number;
}

class MyRaveDb extends Dexie {
  bundles!: Table<StoredBundle, string>;
  favorites!: Table<StoredFavorite, string>;
  pins!: Table<StoredPin, string>;
  assetProgress!: Table<AssetProgress, string>;
  events!: Table<QueuedEventRow, string>;
  tickets!: Table<StoredTicket, string>;
  ticketFiles!: Table<StoredTicketFile, string>;

  constructor() {
    super('myrave');
    this.version(1).stores({
      bundles: 'editionId',
      favorites: 'id, editionId, synced',
      pins: 'id, editionId, synced',
      assetProgress: 'editionId',
    });
    // v2 adds the statistics queue. A new version rather than an edit to v1:
    // a device that already has data must upgrade, not be wiped — it may be
    // holding the only copy of someone's plan.
    this.version(2).stores({
      events: 'id, editionId, queuedAt',
    });
    // v3: your tickets (step 82) and their originals, per rave, for the gate with no signal.
    this.version(3).stores({
      tickets: 'id, editionId',
      ticketFiles: 'id, editionId',
    });
  }
}

export const db = new MyRaveDb();

/** How much of the device's quota this app is using. */
export async function usage(): Promise<{ usedBytes: number; quotaBytes: number } | null> {
  if (!navigator.storage?.estimate) return null;
  try {
    const { usage: used = 0, quota = 0 } = await navigator.storage.estimate();
    return { usedBytes: used, quotaBytes: quota };
  } catch {
    return null;
  }
}

/**
 * Ask the browser not to evict this data.
 *
 * Without it a browser under storage pressure may clear the very lineup
 * someone downloaded specifically because they knew they would have no signal.
 */
export async function requestPersistence(): Promise<boolean> {
  if (!navigator.storage?.persist) return false;
  try {
    return await navigator.storage.persist();
  } catch {
    return false;
  }
}
